import { State, Action, StateContext, Selector, Select } from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import { Injectable } from '@angular/core';
import * as SpecificationActions from './specification.action';
import { SpecificationService } from './specification.service';
import { BannerService } from '../../shared/services/banner.service';
import * as SpecificationModels from '../../models/specification/specification';
import * as SpecificationStatusModels from '../../models/specification/specificationStatus';
import { saveAs } from 'file-saver';
import { HelperService } from '../helper/helper.service';
import * as SpecificationSearchModels from '../../models/specification/specificationSearch';
import * as Models from '../../models/commonModels';
import { StorageService } from '../../shared/services/storage.service';


export interface SpecificationStateModel {
  SpecificationSearch: SpecificationSearchModels.SpecificationSearchModel;
  SpecificationList: SpecificationModels.SpecificationListModel;
  SpecificationListSummary: SpecificationModels.SpecificationListSummaryModel;
  SpecificationStatusesDictionary: SpecificationStatusModels.SpecificationStatusesListModel;
  SpecificationReportFilteredLoading: boolean;
  SpecificationReportDetailedLoadingCustomer: boolean;
  SpecificationReportDetailedLoadingGarage: boolean;
  SpecificationReportDetailedAfterSaleLoading: boolean;
  SpecificationReportReplacementAndSeasoningLoading: boolean;
  SpecificationWorkMode: SpecificationModels.WorkMode;
  PreviewList: Models.ResponseList<SpecificationModels.SpecificationPreviewModel>;
  ImportStatus: any;
  ProcessedReport: string;
  Specification: Models.Response<SpecificationModels.SpecificationModel>;
  GenerateSpecyficationReportStatus: boolean;
  SpecyficationNotes: Models.ResponseList<SpecificationModels.SpecificationNoteModel>;
  SpecificationDetailNotes: Models.ResponseList<SpecificationModels.SpecificationDetailNoteModel>;
  Report: Models.ReportProcessing;
  ExcludeSpecificationIds: number[];
}

class DictionaryKeys {
  public static readonly SpecificationStatusesDictionary = "SpecificationStatusesDictionary";
}

@State<SpecificationStateModel>({
  name: 'SpecificationState',
  defaults: {
    SpecificationSearch: null,
    SpecificationStatusesDictionary: null,
    SpecificationList: null,
    SpecificationListSummary: null,
    SpecificationReportFilteredLoading: false,
    SpecificationReportDetailedLoadingCustomer: false,
    SpecificationReportDetailedLoadingGarage: false,
    SpecificationReportDetailedAfterSaleLoading: false,
    SpecificationReportReplacementAndSeasoningLoading: false,
    SpecificationWorkMode: SpecificationModels.WorkMode.ListMode,
    PreviewList: null,
    ImportStatus: false,
    ProcessedReport: null,
    Specification: null,
    GenerateSpecyficationReportStatus: null,
    SpecyficationNotes: null,
    SpecificationDetailNotes: null,
    Report: null,
    ExcludeSpecificationIds: null
  }
})

@Injectable()
export class SpecificationState {

  constructor(
    private specificationService: SpecificationService,
    private bannerService: BannerService,
    private helperService: HelperService,
    private storageService: StorageService
  ) { }

  PatchReportContext(Name: string, IsProcessing: boolean, Context: StateContext<SpecificationStateModel>) {
    if (IsProcessing) {
      Context.setState(
        patch({
          Report: Models.ReportProcessing.Start(Name),
        })
      );
    }
    else {
      Context.setState(
        patch({
          Report: Models.ReportProcessing.End(Name),
        })
      );
    }
  }

  @Selector()
  static SpecificationSearch(state: SpecificationStateModel) {
    return state.SpecificationSearch;
  }

  @Selector()
  static SpecificationListSummary(state: SpecificationStateModel) {
    return state.SpecificationListSummary;
  }

  @Selector()
  static SpecificationList(state: SpecificationStateModel) {
    return state.SpecificationList;
  }

  @Selector()
  static SpecificationStatusesDictionary(state: SpecificationStateModel) {
    return state.SpecificationStatusesDictionary;
  }

  @Selector()
  static SpecificationReportFilteredLoading(state: SpecificationStateModel) {
    return state.SpecificationReportFilteredLoading;
  }

  @Selector()
  static SpecificationReportDetailedLoadingCustomer(state: SpecificationStateModel) {
    return state.SpecificationReportDetailedLoadingCustomer;
  }

  @Selector()
  static SpecificationReportDetailedLoadingGarage(state: SpecificationStateModel) {
    return state.SpecificationReportDetailedLoadingGarage;
  }

  @Selector()
  static SpecificationReportDetailedAfterSaleLoading(state: SpecificationStateModel) {
    return state.SpecificationReportDetailedAfterSaleLoading;
  }

  @Selector()
  static SpecificationReportReplacementAndSeasoningLoading(state: SpecificationStateModel) {
    return state.SpecificationReportReplacementAndSeasoningLoading;
  }

  @Selector()
  static SpecificationWorkMode(state: SpecificationStateModel) {
    return state.SpecificationWorkMode;
  }

  @Selector()
  static PreviewList(state: SpecificationStateModel) {
    return state.PreviewList;
  }

  @Selector()
  static ImportStatus(state: SpecificationStateModel) {
    return state.ImportStatus;
  }

  @Selector()
  static ProcessedReport(state: SpecificationStateModel) {
    return state.ProcessedReport;
  }

  @Selector()
  static Specification(state: SpecificationStateModel) {
    return state.Specification;
  }

  @Selector()
  static GenerateSpecyficationReportStatus(state: SpecificationStateModel) {
    return state.GenerateSpecyficationReportStatus;
  }

  @Selector()
  static SpecyficationNotes(state: SpecificationStateModel) {
    return state.SpecyficationNotes;
  }

  @Selector()
  static SpecificationDetailNotes(state: SpecificationStateModel) {
    return state.SpecificationDetailNotes;
  }

  @Selector()
  static Report(state: SpecificationStateModel) {
    return state.Report;
  }

  @Selector()
  static ExcludeSpecificationIds(state: SpecificationStateModel) {
    return state.ExcludeSpecificationIds;
  }

  

  @Action(SpecificationActions.GetSpecificationListSummary)
  GetSpecificationListSummary(
    context: StateContext<SpecificationStateModel>,
    action: SpecificationActions.GetSpecificationListSummary
  ) {

    function patchContext(summary: SpecificationModels.SpecificationListSummaryModel) {
      context.setState(
        patch({
          SpecificationListSummary: summary,
        })
      );
    }

    const emptyResponse = new SpecificationModels.SpecificationListSummaryModel();

    patchContext(null);
    this.specificationService.getSummary(action.search).subscribe((summary) => {

      if (summary.Success) {
        patchContext(summary);
      } else {
        patchContext(emptyResponse);
      }
    },
      (error) => {
        patchContext(emptyResponse);
        this.bannerService.error('Błąd pobierania podsumowania listy specyfikacji.');
        this.helperService.SaveException('getSpecificationListSummary', error);
      });
  }

  @Action(SpecificationActions.GetSpecificationReportFiltered)
  GetSpecificationReportFiltered(
    context: StateContext<SpecificationStateModel>,
    action: SpecificationActions.GetSpecificationReportFiltered
  ) {
    function patchContext(loading: boolean) {
      context.setState(
        patch({
          SpecificationReportFilteredLoading: loading
        })
      );
    }

    patchContext(true);


    const fileName = 'Specyfikacje-Wyfiltrowane';

    this.bannerService.info(`Rozpoczęto generowanie pliku raportu ${fileName}, proszę czekać ...`, 'Export danych');

    this.PatchReportContext(action.ReportName, true, context);

    this.specificationService.getReport(action.search, 'filtered').subscribe(
      res => {
        const file = new File([res], `${fileName}-${this.helperService.TimeStamp()}.xlsx`, { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(file);

        this.PatchReportContext(action.ReportName, false, context);
      },
      res => {


        this.PatchReportContext(action.ReportName, false, context);

        this.bannerService.error(`Błąd pobierania raportu: ${fileName}`);
      },
      () => {
        this.PatchReportContext(action.ReportName, false, context);
      }
    );
  }

  @Action(SpecificationActions.GetSpecificationReportReplacementAndSeasoning)
  GetSpecificationReportReplacementAndSeasoning(
    context: StateContext<SpecificationStateModel>,
    action: SpecificationActions.GetSpecificationReportFiltered
  ) {
    function patchContext(loading: boolean) {
      context.setState(
        patch({
          SpecificationReportReplacementAndSeasoningLoading: loading
        })
      );
    }

    patchContext(true);
    const fileName = 'Raport-wymian-i-sezonowan';
    this.bannerService.info(`Rozpoczęto generowanie pliku raportu ${fileName}, proszę czekać ...`, 'Export danych');

    this.specificationService.getReport(action.search, 'ReplacementAndSeasoning').subscribe(
      res => {
        const file = new File([res], `${fileName}-${this.helperService.TimeStamp()}.xlsx`, { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(file);
        patchContext(false);
      },
      res => {
        patchContext(false);
        this.bannerService.error(`Błąd pobierania raportu: ${fileName}`);
      },
      () => {
        patchContext(false);
      }
    );
  }

  @Action(SpecificationActions.GetSpecificationReportDetailed)
  GetSpecificationReportDetailed(
    context: StateContext<SpecificationStateModel>,
    action: SpecificationActions.GetSpecificationReportDetailed
  ) {
    function patchContext(actionType: string, value: boolean) {
      if (actionType === 'CustomerReport') {
        typRaportu = 'Klient_';
        context.setState(
          patch({
            SpecificationReportDetailedLoadingCustomer: value
          })
        );
      } else if (actionType === 'GarageReport' || actionType === 'CustomerGarageReport') {
        typRaportu = 'Serwis_';
        context.setState(
          patch({
            SpecificationReportDetailedLoadingGarage: value
          })
        );
      }
    }

    patchContext(null, null);

    this.PatchReportContext(action.ReportName, true, context);

    // it is not action,type
    const reportType = 'detailed';  //  for filtered => "filtered"
    //  action.type => 'CustomerReport' || 'GarageReport' || 'CustomerGarageReport'
    let typRaportu = null;
    patchContext(action.ReportName, true);

    this.PatchReportContext(action.ReportName, true, context);

    const fileName = 'Specyfikacje-Szczegoly';
    this.bannerService.info(`Rozpoczęto generowanie pliku raportu ${typRaportu}${fileName}, proszę czekać ...`, 'Export danych');

    this.specificationService.getReport(action.search, reportType, action.ReportName).subscribe(
      res => {
        patchContext(action.ReportName, false);
        const file = new File([res], `${fileName}-${typRaportu}${this.helperService.TimeStamp()}.xlsx`, { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(file);

        this.PatchReportContext(action.ReportName, false, context);
      },
      res => {
        patchContext(action.ReportName, false);

        this.PatchReportContext(action.ReportName, false, context);

        this.PatchReportContext(action.ReportName, true, context);

        this.bannerService.error(`Błąd pobierania raportu: ${fileName} - ${typRaportu}`);
      },
      () => {
        patchContext(action.ReportName, false);

        this.PatchReportContext(action.ReportName, false, context);

        this.PatchReportContext(action.ReportName, true, context);
      }
    );
  }

  @Action(SpecificationActions.GetSpecificationReportDetailedAfterSale)
  GetSpecificationReportDetailedAfterSale(
    context: StateContext<SpecificationStateModel>,
    action: SpecificationActions.GetSpecificationReportDetailedAfterSale
  ) {
    function patchContext(loading: boolean) {
      context.setState(
        patch({
          SpecificationReportDetailedAfterSaleLoading: loading
        })
      );
    }

    patchContext(true);
    this.PatchReportContext(action.ReportName, true, context);

    const fileName = 'Specyfikacje-Szczegoly-z-posp';
    this.bannerService.info(`Rozpoczęto generowanie pliku raportu ${fileName}, proszę czekać ...`, 'Export danych');

    this.specificationService.getDetailedAfterSaleReport(action.search).subscribe(
      res => {
        patchContext(false);
        const file = new File([res], `${fileName}-${this.helperService.TimeStamp()}.xlsx`, { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(file);

        this.PatchReportContext(action.ReportName, false, context);
      },
      () => {
        patchContext(false);
        this.PatchReportContext(action.ReportName, false, context);

        this.bannerService.error(`Błąd pobierania raportu: ${fileName}`);
      },
      () => {
        patchContext(false);
        this.PatchReportContext(action.ReportName, false, context);
      }
    );
  }

  // this action state is responsible for clear the upload form
  @Action(SpecificationActions.SetImportStatus)
  SetImportStatus(
    context: StateContext<SpecificationStateModel>,
    action: SpecificationActions.SetImportStatus
  ) {
    context.setState(
      patch({
        ImportStatus: action.status
      })
    );
  }

  @Action(SpecificationActions.GetSpecificationStatusesDictionary)
  GetSpecificationStatusesDictionary(
    context: StateContext<SpecificationStateModel>
  ) {

    const errorMessage = 'Błąd pobierania statusów specyfikacji.';

    let storageKey = DictionaryKeys.SpecificationStatusesDictionary;

    context.setState(
      patch({
        SpecificationStatusesDictionary: this.storageService.getItemJSON(storageKey)
      })
    );

    const state = context.getState();

    if (!state.SpecificationStatusesDictionary) {
      this.specificationService.getStatusesDictionary().subscribe((ret) => {
        if (ret.Success) {

          context.setState(
            patch({
              SpecificationStatusesDictionary: ret
            })
          );

          this.storageService.setItemJSON(storageKey, ret);

        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }
      },
        (error) => {
          this.bannerService.error(errorMessage);
        });
    }
  }

  @Action(SpecificationActions.SetSpecificationSearch)
  SetSpecificationSearch(
    context: StateContext<SpecificationStateModel>,
    action: SpecificationActions.SetSpecificationSearch
  ) {

    action.Search.CurrentPage = 1;
    context.setState(
      patch({
        SpecificationSearch: action.Search
      }));

    context.dispatch(new SpecificationActions.GetSpecificationList(action.Search));
  }

  @Action(SpecificationActions.SetSpecificationListPage)
  SetSpecificationListPage(
    context: StateContext<SpecificationStateModel>,
    action: SpecificationActions.SetSpecificationListPage
  ) {
    context.setState(
      patch({
        SpecificationSearch: action.Search
      }));

    context.dispatch(new SpecificationActions.GetSpecificationList(action.Search));
  }

  @Action(SpecificationActions.ResetSpecificationSearch)
  ResetSpecificationSearch(
    context: StateContext<SpecificationStateModel>
  ) {

    const state = context.getState();

    const newSearch = new SpecificationSearchModels.SpecificationSearchModel();

    newSearch.ItemsPerPage = state.SpecificationSearch.ItemsPerPage;

    context.setState(
      patch({
        SpecificationSearch: newSearch
      }));


    context.dispatch(new SpecificationActions.GetSpecificationList(newSearch));
  }

  @Action(SpecificationActions.NullSpecificationSearch)
  NullSpecificationSearch(
    context: StateContext<SpecificationStateModel>
  ) {


    context.setState(
      patch({
        SpecificationSearch: null
      }));
  }

  @Action(SpecificationActions.GetSpecificationList)
  GetSpecificationList(
    context: StateContext<SpecificationStateModel>,
    action: SpecificationActions.GetSpecificationList
  ) {
    function patchContext(list: SpecificationModels.SpecificationListModel, loading: boolean) {
      context.setState(
        patch({
          SpecificationList: list,
          SpecificationSearch: action.search
        })
      );
    }
    const emptyResponse = new SpecificationModels.SpecificationListModel();

    patchContext(null, true);

    this.specificationService.getList(action.search).subscribe((specs) => {
      if (specs.Success) {
        patchContext(specs, false);
      } else {
        patchContext(emptyResponse, false);
      }
    },
      (error) => {
        patchContext(emptyResponse, false);
        this.bannerService.error('Błąd pobierania listy specyfikacji.');
        this.helperService.SaveException('GetSpecificationList', error);
      });
  }

  @Action(SpecificationActions.SetWorkMode)
  SetWorkMode(
    context: StateContext<SpecificationStateModel>,
    action: SpecificationActions.SetWorkMode

  ) {

    function patchContext(mode: SpecificationModels.WorkMode) {
      context.setState(
        patch({
          SpecificationWorkMode: mode,
        })
      );
    }
    const state = context.getState();

    patchContext(action.Mode);

  }

  @Action(SpecificationActions.HideListItems)
  HideListItems(
    context: StateContext<SpecificationStateModel>,
    action: SpecificationActions.HideListItems

  ) {

    function patchContext(l: SpecificationModels.SpecificationListModel) {
      context.setState(
        patch({
          SpecificationList: new SpecificationModels.SpecificationListModel(
            l.Items, -1, action.Search.PageCount, action.Search.CurrentPage, action.Search.TotalItems),
          SpecificationSearch: action.Search
        })
      );
    }
    const state = context.getState();
    const list = new SpecificationModels.SpecificationListModel(state.SpecificationList ? state.SpecificationList.Items : []);

    action.Specifications.forEach((specification) => {
      list.Items.find((element, index) => {
        if (element.SpecificationId === specification.SpecificationId) {
          list.Items[index].Hide = true;
        }
      });
    });

    action.Search = action.Search || state.SpecificationSearch;
    patchContext(list);

  }

  @Action(SpecificationActions.ShowListItem)
  ShowListItem(
    context: StateContext<SpecificationStateModel>,
    action: SpecificationActions.ShowListItem

  ) {

    function patchContext(l: SpecificationModels.SpecificationListModel) {
      context.setState(
        patch({
          SpecificationList: new SpecificationModels.SpecificationListModel(l.Items, -1),
        })
      );
    }

    const state = context.getState();
    const list = state.SpecificationList;

    list.Items.find((element, index) => {
      if (element.SpecificationId === action.SpecificationId) {
        list.Items[index].Hide = false;
        return;
      }
    });

    patchContext(list);

  }

  @Action(SpecificationActions.ShowAllListItem)
  ShowAllListItem(
    context: StateContext<SpecificationStateModel>,
    action: SpecificationActions.ShowListItem

  ) {

    function patchContext(l: SpecificationModels.SpecificationListModel) {
      context.setState(
        patch({
          SpecificationList: new SpecificationModels.SpecificationListModel(l.Items, -1),
        })
      );
    }

    const state = context.getState();
    const list = state.SpecificationList;

    list.Items.forEach((item) => {
      item.Hide = false;
    });

    patchContext(list);

  }

  @Action(SpecificationActions.GetSpecificationPreviewList)
  GetSpecificationPreviewList(
    context: StateContext<SpecificationStateModel>,
    action: SpecificationActions.GetSpecificationPreviewList
  ) {

    const emptyResponse = new SpecificationModels.SpecificationListModel();

    this.specificationService.getPreviewList(action.search).subscribe((specs) => {
      context.setState(
        patch({
          PreviewList: specs
        }));
    },
      (error) => {
        context.setState(
          patch({
            PreviewList: null
          }));

        this.bannerService.error('Błąd pobierania listy specyfikacji.');
        this.helperService.SaveException('GetSpecificationPreviewList', error);
      });
  }

  @Action(SpecificationActions.ImportAutoryzation)
  ImportAutoryzation(
    context: StateContext<SpecificationStateModel>,
    action: SpecificationActions.ImportAutoryzation
  ) {

    this.bannerService.quickInfo('Rozpoczęto import autoryzacji specyfikacji, proszę czekać.');

    context.dispatch(new SpecificationActions.SetImportStatus(true));

    this.specificationService.importAutorization(action.Data).subscribe(ret => {

      this.helperService.consoleLog(ret.headers);

      const processed = ret.headers.get('x-import-processed');
      const error = ret.headers.get('x-import-error');
      const result = ret.headers.get('x-import-result');
      const message = ret.headers.get('x-import-message');
      const imported = Number(ret.headers.get('x-import-imported'));

      if (result === 'OK') {
        const file = new File([ret.body], `Wynik-Importu-${this.helperService.TimeStamp()}-${action.FileName}`, { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

        if (imported > 0) {
          this.bannerService.instantSuccess(`Zakończono import pliku <b>${action.FileName}</b><br/><b>Zaimportowano ${imported}</b> pozycji.<br/>Przetworzono <b>${processed}</b> wierszy.<br/>Znaleziono <b>${error}</b> wierszy z błędami.`);
        }
        else {
          this.bannerService.instantMessage(`Zakończono import pliku <b>${action.FileName}</b><br/><b>Nie zaimporotwano żadnych pozycji</b>.<br/>Przetworzono <b>${processed}</b> wierszy.<br/>Znaleziono <b>${error}</b> wierszy z błędami.`);
        }

        context.dispatch(new SpecificationActions.SetImportStatus(false));

        saveAs(file);
      } else {

        if (message === 'IMPORT_EXCEPTION_EMPTY_FILE') {
          this.bannerService.instantError(`Zakończono import pliku <b>${action.FileName}</b><br/>Plik przeznaczony do importu jest pusty.`);
        } else if (message === 'IMPORT_EXCEPTION_INVALID_FILE_EXTENSION') {
          this.bannerService.instantError(`Zakończono import pliku <b>${action.FileName}</b><br/>Plik przeznaczony do importu ma niewłaściwe rozszerzenie.`);
        } else if (message === 'IMPORT_EXCEPTION_INVALID_HEDER') {
          this.bannerService.instantError(`Zakończono import pliku <b>${action.FileName}</b><br/>Plik przeznaczony do importu ma niewłaściwy nagłówek.`);
        } else {
          this.bannerService.instantError(`Zakończono import pliku <b>${action.FileName}</b><br/>Wystąpił nieznany błąd podczas przetwarzania pliku.`);
        }
      }
    },
      (error) => {

        this.helperService.SaveException('TyreOrderActions.Import', error);

        this.bannerService.error('Błąd importu autoryzacji specyfikacji.');

        context.dispatch(new SpecificationActions.SetImportStatus(false));
      },
      () => {
        context.dispatch(new SpecificationActions.SetImportStatus(false));
      }
    );
  }

  @Action(SpecificationActions.GetExchangeVerificationReport)
  GetExchangeVerificationReport(
    context: StateContext<SpecificationStateModel>,
    action: SpecificationActions.GetExchangeVerificationReport
  ) {
    this.bannerService.quickInfo('Rozpoczęto generowanie raportu weryfikacji wymian, proszę czekać.');

    this.PatchReportContext(action.ReportName, true, context);

    this.specificationService.getExchangeVerificationReport(action.Data).subscribe(ret => {
      const processed = Number(ret.headers.get('x-import-processed'));
      const result = ret.headers.get('x-import-result');
      const message = ret.headers.get('x-import-message');
      const imported = Number(ret.headers.get('x-import-imported'));

      this.PatchReportContext(action.ReportName, false, context);

      if (result === 'OK') {
        const skipped = Math.max(processed - imported, 0);
        const file = new File([ret.body], `Raport-weryfikacji-wymian-${this.helperService.TimeStamp()}.xlsx`, { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

        this.bannerService.instantSuccess(`Wygenerowano raport <b>${action.FileName}</b><br/>Przetworzono <b>${processed}</b> wierszy.<br/>Zwrócono <b>${imported}</b> pozycji.<br/>Pominięto <b>${skipped}</b> wierszy.`);
        saveAs(file);
      } else if (message === 'IMPORT_EXCEPTION_EMPTY_FILE') {
        this.bannerService.instantError(`Nie można wygenerować raportu <b>${action.FileName}</b><br/>Plik jest pusty.`);
      } else if (message === 'IMPORT_EXCEPTION_INVALID_FILE_EXTENSION') {
        this.bannerService.instantError(`Nie można wygenerować raportu <b>${action.FileName}</b><br/>Plik ma niewłaściwe rozszerzenie.`);
      } else if (message === 'IMPORT_EXCEPTION_INVALID_HEDER') {
        this.bannerService.instantError(`Nie można wygenerować raportu <b>${action.FileName}</b><br/>Plik ma niewłaściwy nagłówek.`);
      } else if (message === 'SEARCH_PARAMETER_INCOMPLETE') {
        this.bannerService.instantError(`Nie można wygenerować raportu <b>${action.FileName}</b><br/>Brakuje wymaganych parametrów raportu.`);
      } else {
        this.bannerService.instantError(`Nie można wygenerować raportu <b>${action.FileName}</b><br/>Wystąpił nieznany błąd podczas przetwarzania.`);
      }
    },
      (error) => {
        this.helperService.SaveException('SpecificationActions.GetExchangeVerificationReport', error);
        this.bannerService.error('Błąd generowania raportu weryfikacji wymian.');
        this.PatchReportContext(action.ReportName, false, context);
      },
      () => {
        this.PatchReportContext(action.ReportName, false, context);
      }
    );
  }

  @Action(SpecificationActions.GetExchangeServicesReport)
  GetExchangeServicesReport(
    context: StateContext<SpecificationStateModel>,
    action: SpecificationActions.GetExchangeServicesReport
  ) {
    this.bannerService.quickInfo('Rozpoczęto generowanie raportu wymian serwisy, proszę czekać.');

    this.PatchReportContext(action.ReportName, true, context);

    this.specificationService.getExchangeServicesReport(action.Data).subscribe(ret => {
      const result = ret.headers.get('x-import-result');
      const message = ret.headers.get('x-import-message');
      const imported = Number(ret.headers.get('x-import-imported'));

      this.PatchReportContext(action.ReportName, false, context);

      if (result === 'OK') {
        const file = new File([ret.body], `Raport-wymian-serwisy-${this.helperService.TimeStamp()}.xlsx`, { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

        this.bannerService.instantSuccess(`Wygenerowano raport <b>Raport wymian serwisy</b><br/>Zwrócono <b>${imported}</b> pozycji.`);
        saveAs(file);
      } else if (message === 'SEARCH_PARAMETER_INCOMPLETE') {
        this.bannerService.instantError('Nie można wygenerować raportu <b>Raport wymian serwisy</b><br/>Brakuje wymaganych parametrów raportu.');
      } else if (message === 'NOTALLOWED_SAVE_EXCEPTION_MESSAGE') {
        this.bannerService.instantError('Nie można wygenerować raportu <b>Raport wymian serwisy</b><br/>Brak uprawnień do wykonania tej operacji.');
      } else {
        this.bannerService.instantError('Nie można wygenerować raportu <b>Raport wymian serwisy</b><br/>Wystąpił nieznany błąd podczas przetwarzania.');
      }
    },
      (error) => {
        this.helperService.SaveException('SpecificationActions.GetExchangeServicesReport', error);
        this.bannerService.error('Błąd generowania raportu wymian serwisy.');
        this.PatchReportContext(action.ReportName, false, context);
      },
      () => {
        this.PatchReportContext(action.ReportName, false, context);
      }
    );
  }

  @Action(SpecificationActions.GetSpecificationReportCost)
  GetDepositeReport(
    context: StateContext<SpecificationStateModel>,
    action: SpecificationActions.GetSpecificationReportCost
  ) {

    this.bannerService.quickInfo('Rozpoczęto generowanie raportu kosztów, proszę czekać.');


    this.specificationService.getCostReport(action.Search).subscribe(ret => {

      context.setState(
        patch({
          ProcessedReport: action.ReportName
        })
      );

      const file = new File([ret], `Koszty pojazdow-${this.helperService.TimeStamp()}.xlsx`, { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(file);
    },
      (error) => {

        this.helperService.SaveException('SpecificationActions.GetSpecificationReportCost', error);

        this.bannerService.error('Błąd pobierania raportu depozytów.');
      },
      () => {

        context.setState(
          patch({
            ProcessedReport: action.ReportName
          })
        );
      }
    );
  }

  @Action(SpecificationActions.GetSpecificationReportAutorizationCost)
  GetAutorizationReport(
    context: StateContext<SpecificationStateModel>,
    action: SpecificationActions.GetSpecificationReportCost
  ) {

    this.bannerService.quickInfo('Rozpoczęto generowanie raportu po autoryzacjach, proszę czekać.');

    this.specificationService.getAutoryzationCostReport(action.Search).subscribe(ret => {

      const file = new File([ret], `Koszty po autoryzacji-${this.helperService.TimeStamp()}.xlsx`, { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(file);
    },
      (error) => {

        this.helperService.SaveException('SpecificationActions.GetSpecificationReportAutorizationCost', error);

        this.bannerService.error('Błąd pobierania raportu kosztów po autoryzacjach.');
      },
      () => {

        context.setState(
          patch({
            ProcessedReport: action.ReportName
          })
        );
      }
    );
  }

  @Action(SpecificationActions.New)
  New(
    context: StateContext<SpecificationStateModel>,
    action: SpecificationActions.New
  ) {
    const errorMessage = 'Błąd utworzenia specyfikacji.';

    this.specificationService.new(action.Specificatoin, action.FileUpload1, action.FileUpload2).subscribe(
      ret => {

        action.Specificatoin.End();

        if (ret.Success) {
          if (ret.Item) {
            this.bannerService.success('Utworzono specyfikację ' + ret.Item.Number + '.');
          }
          else {
            this.bannerService.error(errorMessage);
          }

          context.setState(
            patch({
              Specification: ret
            })
          );
        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }
      },

      (error) => {

        this.helperService.SaveException('SpecificationActions.New', error);

        this.bannerService.error(errorMessage);
      },
      () => { });
  }

  @Action(SpecificationActions.Set)
  Set(
    context: StateContext<SpecificationStateModel>,
    action: SpecificationActions.Set
  ) {

    const errorMessage = 'Błąd zapisu zmian do specyfikacji.';

    this.specificationService.set(action.Specificatoin).subscribe(
      ret => {

        action.Specificatoin.End();

        if (ret.Success) {
          if (ret.Item) {
            this.bannerService.success('Zapisano zmiany do specyfikacji.');

            if (ret.Message && ret.Message.length > 0) {
              this.bannerService.instantMessage(ret.Message);
            } if (ret.Message && ret.Message.length > 0) {
              this.bannerService.instantMessage(ret.Message);
            }
          }
          else {
            this.bannerService.error(errorMessage);
          }

          context.setState(
            patch({
              Specification: ret
            })
          );
        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }
      },

      (error) => {

        this.helperService.SaveException('SpecificationActions.Set', error);

        this.bannerService.error(errorMessage);
      },
      () => { });
  }

  @Action(SpecificationActions.Get)
  Get(
    context: StateContext<SpecificationStateModel>,
    action: SpecificationActions.Get
  ) {
    const errorMessage = 'Błąd pobrania szczegółów specyfikacji.';

    this.specificationService.get(action.SpecificatoinId).subscribe(
      ret => {

        action.SpecificatoinId.End();

        if (ret.Success && ret.Item) {

          context.setState(
            patch({
              Specification: ret
            })
          );
        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }
      },

      (error) => {

        this.helperService.SaveException('SpecificationActions.Get', error);

        this.bannerService.error(errorMessage);
      },
      () => { });
  }

  @Action(SpecificationActions.NewSpecificationDetails)
  NewSpecificationDetails(
    context: StateContext<SpecificationStateModel>,
    action: SpecificationActions.NewSpecificationDetails
  ) {
    const errorMessage = 'Błąd dodania nowej usługi do specyfikacji.';

    this.specificationService.newSpecificationDetail(action.Specificatoin).subscribe(
      ret => {

        action.Specificatoin.End();

        if (ret.Success) {
          if (ret.Item) {
            this.bannerService.success('Dadano nową usługę.');

            if (ret.Message && ret.Message.length > 0) {
              this.bannerService.instantMessage(ret.Message);
            }
          }
          else {
            this.bannerService.error(errorMessage);
          }

          context.setState(
            patch({
              Specification: ret
            })
          );
        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }
      },

      (error) => {

        this.helperService.SaveException('SpecificationActions.NewSpecificationDetails', error);

        this.bannerService.error(errorMessage);
      },
      () => { });
  }

  @Action(SpecificationActions.DeleteSpecificationDetails)
  DeleteSpecificationDetails(
    context: StateContext<SpecificationStateModel>,
    action: SpecificationActions.DeleteSpecificationDetails
  ) {
    const errorMessage = 'Błąd usunięcia usługi ze specyfikacji.';

    this.specificationService.deleteSpecificationDetail(action.Specificatoin).subscribe(
      ret => {

        action.Specificatoin.End();

        if (ret.Success) {
          if (ret.Item) {
            this.bannerService.success('Usunięto wybraną usługę.');

            if (ret.Message && ret.Message.length > 0) {
              this.bannerService.instantMessage(ret.Message);
            }
          }
          else {
            this.bannerService.error(errorMessage);
          }

          context.setState(
            patch({
              Specification: ret
            })
          );
        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }
      },

      (error) => {

        this.helperService.SaveException('SpecificationActions.DeleteSpecificationDetails', error);

        this.bannerService.error(errorMessage);
      },
      () => { });
  }

  @Action(SpecificationActions.AccepSpecificationDetails)
  AccepSpecificationDetails(
    context: StateContext<SpecificationStateModel>,
    action: SpecificationActions.DeleteSpecificationDetails
  ) {
    const errorMessage = 'Błąd autoryzacji usługi.';

    this.specificationService.acceptSpecificationDetail(action.Specificatoin).subscribe(
      ret => {

        action.Specificatoin.End();

        if (ret.Success) {
          if (ret.Item) {
            this.bannerService.success('Zautoryzowano usługę');

            if (ret.Message && ret.Message.length > 0) {
              this.bannerService.instantMessage(ret.Message);
            }
          }
          else {
            this.bannerService.error(errorMessage);
          }

          context.setState(
            patch({
              Specification: ret
            })
          );
        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }
      },

      (error) => {

        this.helperService.SaveException('SpecificationActions.AccepSpecificationDetails', error);

        this.bannerService.error(errorMessage);
      },
      () => { });
  }

  @Action(SpecificationActions.RejectSpecificationDetails)
  RejectSpecificationDetails(
    context: StateContext<SpecificationStateModel>,
    action: SpecificationActions.DeleteSpecificationDetails
  ) {
    const errorMessage = 'Błąd odrzucenia usługi.';

    this.specificationService.rejectSpecificationDetail(action.Specificatoin).subscribe(
      ret => {

        action.Specificatoin.End();

        if (ret.Success) {
          if (ret.Item) {
            this.bannerService.success('Odrzucono usługę.');

            if (ret.Message && ret.Message.length > 0) {
              this.bannerService.instantMessage(ret.Message);
            }
          }
          else {
            this.bannerService.error(errorMessage);
          }

          context.setState(
            patch({
              Specification: ret
            })
          );
        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }
      },

      (error) => {

        this.helperService.SaveException('SpecificationActions.RejectSpecificationDetails', error);

        this.bannerService.error(errorMessage);
      },
      () => { });
  }


  @Action(SpecificationActions.SetSpecificationDetails)
  SetSpecificationDetails(
    context: StateContext<SpecificationStateModel>,
    action: SpecificationActions.DeleteSpecificationDetails
  ) {
    const errorMessage = 'Błąd aktualizacji usługi ze specyfikacji.';

    this.specificationService.setSpecificationDetail(action.Specificatoin).subscribe(
      ret => {

        action.Specificatoin.End();

        if (ret.Success) {
          if (ret.Item) {
            this.bannerService.success('Zaktualizowano szczegóły usługi.');

            if (ret.Message && ret.Message.length > 0) {
              this.bannerService.instantMessage(ret.Message);
            }
          }
          else {
            this.bannerService.error(errorMessage);
          }

          context.setState(
            patch({
              Specification: ret
            })
          );
        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }
      },

      (error) => {

        this.helperService.SaveException('SpecificationActions.SetSpecificationDetails', error);

        this.bannerService.error(errorMessage);
      },
      () => { });
  }

  @Action(SpecificationActions.GetFile)
  GetFile(
    context: StateContext<SpecificationStateModel>,
    action: SpecificationActions.GetFile
  ) {

    this.bannerService.info('Rozpoczęto pobieranie dokumentu');

    this.specificationService.getFile(action.Specificatoin).subscribe(
      res => {

        const file = new File([res.body], this.helperService.GetFileNameFromHttpResponse(res), { type: res.headers.get('Content-Type') });

        saveAs(file);
      },
      res => {
        this.bannerService.error(`Błąd pobierania pliku.`);
      },
      () => {
        this.helperService.consoleLog('SpecificationActions.GetFile');
      }
    );
  }

  @Action(SpecificationActions.DeleteFile)
  DeleteFile(
    context: StateContext<SpecificationStateModel>,
    action: SpecificationActions.DeleteFile
  ) {

    const errorMessage = 'Błąd zapisu zmian do specyfikacji.';

    this.specificationService.deleteFile(action.Specificatoin).subscribe(
      ret => {

        action.Specificatoin.End();

        if (ret.Success) {
          if (ret.Item) {
            this.bannerService.success('Zapisano zmiany do specyfikacji.');

            if (ret.Message && ret.Message.length > 0) {
              this.bannerService.instantMessage(ret.Message);
            } if (ret.Message && ret.Message.length > 0) {
              this.bannerService.instantMessage(ret.Message);
            }
          }
          else {
            this.bannerService.error(errorMessage);
          }

          context.setState(
            patch({
              Specification: ret
            })
          );
        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }
      },

      (error) => {

        this.helperService.SaveException('SpecificationActions.DeleteFile', error);

        this.bannerService.error(errorMessage);
      },
      () => { });
  }

  @Action(SpecificationActions.SetFile)
  SetFile(
    context: StateContext<SpecificationStateModel>,
    action: SpecificationActions.SetFile
  ) {

    const errorMessage = 'Błąd zapisu zmian do specyfikacji.';

    this.specificationService.setFile(action.Specificatoin, action.FileIndex, action.File).subscribe(
      ret => {

        action.Specificatoin.End();

        if (ret.Success) {
          if (ret.Item) {
            this.bannerService.success('Zapisano zmiany do specyfikacji.');

            if (ret.Message && ret.Message.length > 0) {
              this.bannerService.instantMessage(ret.Message);
            } if (ret.Message && ret.Message.length > 0) {
              this.bannerService.instantMessage(ret.Message);
            }
          }
          else {
            this.bannerService.error(errorMessage);
          }

          context.setState(
            patch({
              Specification: ret
            })
          );
        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }
      },

      (error) => {

        this.helperService.SaveException('SpecificationActions.SetFile', error);

        this.bannerService.error(errorMessage);
      },
      () => { });
  }

  @Action(SpecificationActions.GenerateSpecyficationReport)
  GenerateSpecyficationReport(
    context: StateContext<SpecificationStateModel>,
    action: SpecificationActions.GenerateSpecyficationReport
  ) {

    context.setState(
      patch({
        GenerateSpecyficationReportStatus: null
      })
    );

    this.bannerService.quickInfo('Rozpoczęto generowanie raportu, proszę czekać.');

    this.specificationService.generateSpecyficationReport(action.Specyfication).subscribe(ret => {
      const file = new File([ret], `Specyfikacja-${this.helperService.TimeStamp()}.pdf`, { type: 'application/pdf' });
      saveAs(file);

      // context.setState(
      //   patch({
      //     GenerateSpecyficationReportStatus: true
      //   })
      // );


      const fileURL = URL.createObjectURL(file);
      window.open(fileURL, '_blank');
    },
      (error) => {
        // context.setState(
        //   patch({
        //     GenerateSpecyficationReportStatus: false
        //   })
        // );

        this.helperService.SaveException('SpecificationActions.GenerateSpecyficationReport', error);

        this.bannerService.error('Błąd pobierania pliku pdf.');

      },
      () => { }
    );
  }

  @Action(SpecificationActions.Create)
  Create(
    context: StateContext<SpecificationStateModel>,
    action: SpecificationActions.Create
  ) {
    const errorMessage = 'Błąd utworzenia specyfikacji.';

    this.specificationService.create(action.Specificatoin).subscribe(
      ret => {

        action.Specificatoin.End();

        if (ret.Success) {
          if (ret.Item) {
            this.bannerService.success('Przygotowano specyfikację ' + ret.Item.Number + ' do wypełnienia.');
          }
          else {
            this.bannerService.error(errorMessage);
          }

          context.setState(
            patch({
              Specification: ret
            })
          );
        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }
      },

      (error) => {

        this.helperService.SaveException('SpecificationActions.Create', error);

        this.bannerService.error(errorMessage);
      },
      () => { });
  }

  @Action(SpecificationActions.ChangeCar)
  ChangeCar(
    context: StateContext<SpecificationStateModel>,
    action: SpecificationActions.ChangeCar
  ) {
    const errorMessage = 'Błąd zmiany samochodu przypisanego do specyfikacji.';

    this.specificationService.changeCar(action.Specificatoin).subscribe(
      ret => {

        action.Specificatoin.End();

        if (ret.Success) {
          if (ret.Item) {
            this.bannerService.success('Zmieniono samochód przypisany do specyfikacji.');

            if (ret.Message && ret.Message.length > 0) {
              this.bannerService.instantMessage(ret.Message);
            }
          }
          else {
            this.bannerService.error(errorMessage);
          }

          context.setState(
            patch({
              Specification: ret
            })
          );
        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }
      },

      (error) => {

        this.helperService.SaveException('SpecificationActions.ChangeCar', error);

        this.bannerService.error(errorMessage);
      },
      () => { });
  }

  @Action(SpecificationActions.SetSpecificationNote)
  SetSpecificationNote(
    context: StateContext<SpecificationStateModel>,
    action: SpecificationActions.SetSpecificationNote
  ) {
    const errorMessage = 'Błąd wysłania wiadomości do specyfikacji.';

    this.specificationService.setSpecificationNote(action.SpecificationNote).subscribe(
      ret => {

        if (ret.Success) {
          this.bannerService.success('Wysłano nową wiadomość do specyfikacji.');

          context.setState(
            patch({
              SpecyficationNotes: ret
            })
          );
        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }
      },

      (error) => {

        this.helperService.SaveException('SpecificationActions.SetSpecificationNote', error);

        this.bannerService.error(errorMessage);
      },
      () => { });
  }

  @Action(SpecificationActions.GetSpecificationNotes)
  GetSpecificationNotes(
    context: StateContext<SpecificationStateModel>,
    action: SpecificationActions.GetSpecificationNotes
  ) {
    const errorMessage = 'Błąd odczytu wiadomości specyfikacji.';

    this.specificationService.getSpecificationNotes(action.SpecificatoinId).subscribe(
      ret => {

        if (ret.Success) {

          context.setState(
            patch({
              SpecyficationNotes: ret
            })
          );
        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }
      },

      (error) => {

        this.helperService.SaveException('SpecificationActions.GetSpecificationNotes', error);

        this.bannerService.error(errorMessage);
      },
      () => { });
  }

  @Action(SpecificationActions.SetSpecificationDetailNote)
  SetSpecificationDetailNote(
    context: StateContext<SpecificationStateModel>,
    action: SpecificationActions.SetSpecificationDetailNote
  ) {
    const errorMessage = 'Błąd wysłania wiadomości do usługi.';

    this.specificationService.setSpecificationDetailNote(action.SpecificationDetailNote).subscribe(
      ret => {

        if (ret.Success) {
          this.bannerService.success('Wysłano nową wiadomość do usługi.');

          context.setState(
            patch({
              SpecificationDetailNotes: ret
            })
          );
        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }
      },

      (error) => {

        this.helperService.SaveException('SpecificationActions.SetSpecificationDetailNote', error);

        this.bannerService.error(errorMessage);
      },
      () => { });
  }

  @Action(SpecificationActions.GetSpecificationDetailNotes)
  GetSpecificationDetailNotes(
    context: StateContext<SpecificationStateModel>,
    action: SpecificationActions.GetSpecificationDetailNotes
  ) {
    const errorMessage = 'Błąd odczytu wiadomości usługi.';

    this.specificationService.getSpecificationDetailNotes(action.SpecificationDetailId).subscribe(
      ret => {

        if (ret.Success) {

          context.setState(
            patch({
              SpecificationDetailNotes: ret
            })
          );
        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }
      },

      (error) => {

        this.helperService.SaveException('SpecificationActions.GetSpecificationDetailNotes', error);

        this.bannerService.error(errorMessage);
      },
      () => { });
  }

  @Action(SpecificationActions.UpdateExcludeSpecificationIds)
  UpdateExcludeSpecificationIds(
    context: StateContext<SpecificationStateModel>,
    action: SpecificationActions.UpdateExcludeSpecificationIds

  ) {

    context.setState(
      patch({
        ExcludeSpecificationIds: action.ExcludeSpecificationIds
      })
    );
  }
}
